// BondInfoGrid.jsx
import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { IconButton, Box, CircularProgress, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';

const BondInfoGrid = ({
  transactionId,
  onEditBondInfo, // Callback to open the modal with selected bond info
  triggerRefetch, // Dependency to trigger refetch when data changes
  canEdit,
  onBondInfoUpdate
}) => {
  const [bondInfoList, setBondInfoList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch transfer powers
  const fetchTransferPowers = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('jwtToken');
      const baseURL = process.env.REACT_APP_BASE_URL;

      const response = await fetch(
        `${baseURL}PostingTransferBond/${transactionId}/transferPowers`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch transfer powers.');
      }
      const data = await response.json();
      // Assuming data is an array of TransferPowersDto with powerNumber as unique identifier
      const formattedData = data.map((power, index) => ({
        transferBondId: power.transferBondId,
        id: index+1, // Using powerNumber as the unique 'id' field
        powerId: power.powerId,
        powerNumber: power.powerNumber || 'N/A',
        bondAmount: power.bondAmount,
        caseNumber: power.caseNumber,
        executionDate: power.executionDate,
        courtDate: power.courtDate,
        charges: power.charges,
        formattedBondAmount:
          typeof power.bondAmount === 'number'
            ? '$' + power.bondAmount.toFixed(2)
            : '$' + parseFloat(power.bondAmount).toFixed(2) || '$0.00',
        formattedExecutionDate: power.executionDate
          ? new Date(power.executionDate).toLocaleDateString()
          : '-',
        formattedCourtDate: power.courtDate
          ? new Date(power.courtDate).toLocaleDateString()
          : '-',
        formattedCaseNumber: power.caseNumber || '-',
        formattedCharges: power.charges || '-',
        chargeType: power.chargeType || '',
      }));

      // Check for duplicate or missing ids
      const uniqueIds = new Set();
      const cleanedData = formattedData.filter((row) => {
        if (!row.id) {
          toast.error('Encountered a bond info entry without a Power Number.');
          return false;
        }
        uniqueIds.add(row.id);
        return true;
      });

      setBondInfoList(cleanedData);
      console.log('Bond Info List:', cleanedData);

      if (onBondInfoUpdate) {
        onBondInfoUpdate(cleanedData);
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Error fetching transfer powers.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (transactionId) {
      fetchTransferPowers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionId, triggerRefetch]);

  const handleEdit = (row) => {
    onEditBondInfo(row);
  };

  const handleRemove = async (row) => {
    // Confirm deletion
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this bond information?'
    );
    if (!confirmDelete) return;
    try {
      const token = localStorage.getItem('jwtToken');
      const baseURL = process.env.REACT_APP_BASE_URL;
      const response = await fetch(
        `${baseURL}PostingTransferBond/${transactionId}/transferPowers/${row.transferBondId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete transfer power.');
      }

      toast.success('Transfer power deleted successfully.');
      fetchTransferPowers(); // Refetch after deletion
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Error deleting transfer power.');
    }
  };

  const columns = [
    {
      field: 'powerNumber',
      headerName: 'Power Number',
      width: 150,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'formattedBondAmount',
      headerName: 'Bond Amount',
      width: 130,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'formattedCharges',
      headerName: 'Charges',
      width: 200,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'formattedCaseNumber',
      headerName: 'Case Number',
      width: 150,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'formattedExecutionDate',
      headerName: 'Execution Date',
      width: 150,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'formattedCourtDate',
      headerName: 'Court Date',
      width: 150,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => (
        !canEdit ? ( // Replace 'canEdit' with your boolean condition
          <Box>
            <Tooltip title="Edit Bond Information">
              <IconButton color="primary" onClick={() => handleEdit(params.row)}>
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Bond Information">
              <IconButton color="error" onClick={() => handleRemove(params.row)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null // No actions if 'canEdit' is false
      ),
    },
  ];

  return (
    <Box sx={{ width: '100%',  mt: 2 }}>
      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <DataGrid
          rows={bondInfoList}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[5]}
          disableSelectionOnClick
          getRowId={(row) => row.id}
          autoHeight
        />
      )}
    </Box>
  );
};

export default BondInfoGrid;
